// Import Libraries
import React, { useState, useEffect } from "react";
import PageSEO from "../../../../Components/SEO/PageSEO";
import { getProducts } from "../../../../ApiLayer/apiLayer";

// Import Components
import ServerPageIntro from "../../../../Components/ServerPageIntro/ServerPageIntro";
import FilterComponent from "../../../../Components/FilterComponent/FilterComponent";
import ServerStandardsComponent from "../../../../Components/ServerStandardsComponent/ServerStandardsComponent";
import DedicatedServerLocations from "../StandardServersPage/Components/DedicatedServerLocations/DedicatedServerLocations";
import TestNetworkSpeedsComponent from "../StandardServersPage/Components/TestNetworkSpeedsComponent/TestNetworkSpeedsComponent";

// Import Assets
import StorageServer from "../../../../Assets/Storage_Server.png";

// Import Animation
import FadeInOnScroll from "../../../../util/Animations/FadeInOnScroll";

const StorageServerPage = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getProducts();
        if (response.products) {
          // Filter for storage servers
          const storageServers = response.products.filter(product => {
            const isStorageType = product.customdata?.type?.toLowerCase().includes('storage');
            // Also check for high storage capacity in the storage field
            const storageInfo = product.customdata?.storage?.toLowerCase() || '';
            const hasHighStorage = storageInfo.includes('tb') || 
              (storageInfo.match(/\d+gb/g) || []).some(cap => parseInt(cap) >= 1000);
            return isStorageType || hasHighStorage;
          });
          setProducts(storageServers);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  // Helper function to extract storage configurations
  const getStorageConfigs = () => {
    const configs = new Set();
    products.forEach(product => {
      if (product.customdata?.storage) {
        configs.add(product.customdata.storage);
      }
    });
    return Array.from(configs);
  };

  // Helper function to get unique drive types
  const getDriveTypes = () => {
    const types = new Set();
    products.forEach(product => {
      const storage = product.customdata?.storage?.toLowerCase() || '';
      if (storage.includes('nvme')) types.add('NVMe');
      if (storage.includes('ssd')) types.add('SSD');
      if (storage.includes('hdd')) types.add('HDD');
    });
    return Array.from(types);
  };

  // Define structured data for the page
  const storageServersSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "WebNX Storage Dedicated Servers",
    "description": "Enterprise-grade storage servers with high-capacity storage options, hardware RAID controllers, and 10Gbps networking. Perfect for high-volume data, backups, media storage, and databases.",
    "brand": {
      "@type": "Brand",
      "name": "WebNX"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "offers": products.map(product => ({
        "@type": "Offer",
        "name": `Storage Server with ${product.customdata?.storage || 'High Capacity Storage'}`,
        "description": `Storage Server featuring ${product.customdata?.storage || ''}, ${product.customdata?.processor || ''}, ${product.customdata?.memory || ''}, ${product.customdata?.network || ''}`,
        "price": product.pricing?.USD?.monthly || "",
        "priceCurrency": "USD",
        "availability": product.stockcontrol === "true" && product.stocklevel > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "url": product.product_url || "",
        "itemCondition": "https://schema.org/NewCondition",
        "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "seller": {
          "@type": "Organization",
          "name": "WebNX"
        }
      }))
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Server Type",
        "value": "Storage Dedicated"
      },
      {
        "@type": "PropertyValue",
        "name": "Available Storage Configurations",
        "value": getStorageConfigs().join(", ")
      },
      {
        "@type": "PropertyValue",
        "name": "Drive Types",
        "value": getDriveTypes().join(", ")
      },
      {
        "@type": "PropertyValue",
        "name": "RAID Support",
        "value": "Hardware RAID Controllers with Multiple Configuration Options"
      },
      {
        "@type": "PropertyValue",
        "name": "Network Connectivity",
        "value": "10Gbps with Jumbo Frame Support"
      },
      {
        "@type": "PropertyValue",
        "name": "Data Protection",
        "value": "Hot-swappable drives, Hardware RAID, Encryption Support"
      }
    ],
    "applicationCategory": [
      "Data Storage",
      "Backup Solutions",
      "Media Storage",
      "Database Hosting",
      "Content Delivery",
      "Archive Storage"
    ]
  };

  return (
    <div>
      <PageSEO
        title="Storage Servers | Enterprise-Grade Data Storage Solutions by WebNX"
        description="Deploy high-capacity storage servers with WebNX. Featuring hardware RAID controllers, 10Gbps networking, and enterprise-grade data protection. Perfect for backups, media storage, and databases."
        keywords={`storage servers, enterprise storage solutions, ${getDriveTypes().join(', ')} storage, RAID storage, backup servers, media storage servers, database servers, high-capacity storage, data center storage`}
        path="/storage-servers"
        image="https://webnx.com/assets/storage-server.jpg"
        schema={storageServersSchema}
        socialProfiles={{
          facebook: "https://www.facebook.com/WebNX/",
          linkedin: "https://www.linkedin.com/company/webnx/"
        }}
      />

      <FadeInOnScroll>
        <ServerPageIntro
          header="WebNX Storage Servers"
          subheader="Enterprise-Grade Storage Solutions"
          introText="Meet your growing data storage needs with WebNX's custom-built storage servers. Designed for high-volume data, backups, media files, and databases, our solutions deliver the perfect combination of capacity and performance. With trusted hardware from industry leaders and our optimized network infrastructure, your data remains accessible, secure, and performing at its best."
          image={StorageServer}
          hideImageOnMobile={true}
          features={[
            {
              icon: "FaHdd",
              title: "Massive Storage",
              description: "Up to 100TB+ raw storage with RAID configuration options"
            },
            {
              icon: "FaDatabase",
              title: "Data Protection",
              description: "Hardware RAID controllers and hot-swappable drive bays"
            },
            {
              icon: "FaExchangeAlt",
              title: "High Throughput",
              description: "10Gbps networking with jumbo frame support for optimal data transfer"
            },
            {
              icon: "FaLock",
              title: "Data Security",
              description: "Encryption at rest and in transit with secure remote management"
            }
          ]}
        />
      </FadeInOnScroll>

      {/* Filter Component */}
      <FilterComponent defaultFilters={{ "Server Type": ["Storage"] }} />

      <FadeInOnScroll>
        <ServerStandardsComponent />
      </FadeInOnScroll>

      <FadeInOnScroll>
        <DedicatedServerLocations />
      </FadeInOnScroll>

      <FadeInOnScroll>
        <TestNetworkSpeedsComponent />
      </FadeInOnScroll>
    </div>
  );
};

export default StorageServerPage;

